<template>
    <div class="refund-feedback-block">
        <div class="block-header">
            <h3 id="refund-reason-title" class="block-title">Why are you requesting a refund?</h3>
            <p class="block-description">
                Please let us know why you'd like to refund your license. This helps us improve our service.
            </p>
        </div>

        <div class="feedback-options">
            <div class="radio-options" role="radiogroup" aria-labelledby="refund-reason-title">
                <label 
                    v-for="option in feedbackOptions" 
                    :key="option.value"
                    class="radio-option"
                    :class="{ 'selected': selectedReason === option.value }"
                >
                    <input
                        v-model="selectedReason"
                        type="radio"
                        name="refund-reason"
                        class="sr-only"
                        :value="option.value"
                    >
                    <div class="radio-circle">
                        <div v-if="selectedReason === option.value" class="radio-dot"></div>
                    </div>
                    <span class="radio-label">{{ option.label }}</span>
                </label>
            </div>
        </div>

        <ModalField
            v-if="selectedReason === 'other'"
            class="additional-feedback"
            label="Please provide additional details"
            input-id="refund-additional-feedback"
        >
            <textarea
                id="refund-additional-feedback"
                v-model="additionalFeedback"
                class="feedback-textarea"
                rows="3"
                placeholder="Tell us more about your reason for requesting a refund..."
            ></textarea>
        </ModalField>

        <div class="action-buttons">
            <PrimeButton label="Cancel" variant="secondary" class="p-btn-root_secondary" @click="$emit('close')" />
            <PrimeButton label="Continue" @click="continueToNext" :disabled="!selectedReason" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import ModalField from '~/components/modals/shared/ModalField.vue';

interface Props {
    license?: any;
}

const props = defineProps<Props>();

const emit = defineEmits<{
    feedback: [feedback: any];
    goToPage: [page: number];
    close: [];
}>();

const selectedReason = ref('');
const additionalFeedback = ref('');

const feedbackOptions = [
    { label: 'Too expensive', value: 'too_expensive' },
    { label: 'Not using the service', value: 'not_using' },
    { label: 'Found a better alternative', value: 'better_alternative' },
    { label: 'Technical issues', value: 'technical_issues' },
    { label: 'Customer service issues', value: 'customer_service' },
    { label: 'Other', value: 'other' }
];

const continueToNext = () => {
    const feedback = {
        reason: selectedReason.value,
        additionalFeedback: additionalFeedback.value,
        licenseId: props.license?.id
    };
    
    emit('feedback', feedback);
    emit('goToPage', 2);
};
</script>

<style lang="scss" scoped>
.refund-feedback-block {
    @apply p-6;
}

.block-header {
    @apply mb-6 text-center;
    
    .block-title {
        @apply text-2xl font-semibold text-gray-900 mb-2;
    }
    
    .block-description {
        @apply text-gray-600 text-base;
    }
}

.feedback-options {
    @apply mb-6;
}

.radio-options {
    @apply space-y-3;
}

.radio-option {
    @apply relative flex items-center cursor-pointer p-3 rounded-lg border border-gray-200 transition-all duration-200;
    
    &:hover {
        @apply border-gray-300 bg-gray-50;
    }
    
    &.selected {
        @apply border-blue-500 bg-blue-100;
    }
    
    .radio-circle {
        @apply w-5 h-5 rounded-full border-2 border-gray-300 flex items-center justify-center mr-3 flex-shrink-0;
        
        .radio-dot {
            @apply w-2.5 h-2.5 rounded-full bg-blue-500;
        }
    }
    
    &.selected .radio-circle {
        @apply border-blue-500;
    }
    
    &:has(input:focus-visible) .radio-circle {
        @apply ring-2 ring-blue-500 ring-offset-1;
    }
    
    .radio-label {
        @apply text-gray-700 font-medium;
    }
    
    &.selected .radio-label {
        @apply text-blue-700;
    }
}

.additional-feedback {
    @apply mb-6;

    .feedback-textarea {
        @apply w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none;
        min-height: 80px;
        
        &::placeholder {
            @apply text-gray-400;
        }
    }
}

.action-buttons {
    @apply flex justify-center gap-3;
    
    .btn {
        @apply px-6 py-2 rounded-lg font-medium transition-all duration-200;
        
        &:disabled {
            @apply opacity-50 cursor-not-allowed;
        }
        
        &.btn-outline {
            @apply border border-gray-300 text-gray-700 bg-white;
            
            &:hover:not(:disabled) {
                @apply border-gray-400 bg-gray-50;
            }
        }
        
        &.btn-primary {
            @apply bg-blue-500 text-ink-invert border border-blue-500;
            
            &:hover:not(:disabled) {
                @apply bg-blue-600 border-blue-600;
            }
        }
    }
}
</style> 